import { GAME_ASSET_SLUG } from "@/config/game";
import { ChemistryChip } from "@/design-system/Chemistry/Chemistry";
import { EvolutionFooter } from "@/design-system/Evolution/EvolutionFooter";
import { arrow } from "@/designSystemIcons";
import { extractColors } from "@/functions/extractCardColors";
import { EvolutionsIcon } from "@/icons";
import { cn } from "@/lib/utils";
// Type-only imports — erased by the bundler, so this presentational module never
// pulls the (API-coupled) Redux slices into the design-system bundle.
import type { VersionAssets } from "@/redux/loadedContent/loadedContentSlice";
import type { Tier as TierType } from "@/redux/players/playersSlice";
import { chemStylesToIcon } from "@/redux/squadBuilder/squadBuilderUtils";
import type { ContentLabels } from "@repo/shared/api";
import type { EvolutionFooterDetails } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import {
  BasePlayer,
  Chemistry,
  EventToken,
  Player,
  isEvolution as isEvolutionFunction,
  isEvolutionPlayer,
} from "@repo/shared/types";
import { usesFullPlayerImage } from "@repo/shared/utils";
import { FACE_STAT_KEYS } from "@repo/shared/utils";
import { Plus } from "lucide-react";
import { ComponentProps } from "react";
import { NavLink } from "react-router";

import { PlayStylePlus } from "../../components/molecules/Player/PlayStylePlus/PlayStylePlus";
import Card from "../Card/Card";
import { ContentTag } from "../Tags/ContentTag/ContentTag";
import { MetaRatingChip } from "../Tags/MetaRatingChip/MetaRatingChip";
import { NewTag } from "../Tags/NewTag/NewTag";
import { CardEffects } from "./CardEffects";
import { Price } from "./Price";

const CARD_ART_WIDTH = 516;
const CARD_ART_HEIGHT = 716;
/** The card's box at scale 1; placeholders that must not shift reserve this. */
export const PLAYER_CARD_BASE_WIDTH = 180;
export const PLAYER_CARD_BASE_HEIGHT = Math.round(
  (PLAYER_CARD_BASE_WIDTH * CARD_ART_HEIGHT) / CARD_ART_WIDTH,
);
/** UltimateTeamIcons glyph EA puts under the position of a first-owner card. */
export const FIRST_OWNER_GLYPH = "\ue02e";
const BASE_WIDTH = PLAYER_CARD_BASE_WIDTH;
const BASE_HEIGHT = PLAYER_CARD_BASE_HEIGHT;
const ATTRS = ["PAC", "SHO", "PAS", "DRI", "DEF", "PHY"];

const FONT_MEDIUM = "CruyffMedium";
const FONT_CONDENSED = "CruyffCondensedMedium";
const FONT_NUMBERS_MEDIUM = "CruyffNumbersMedium";
const FONT_REGULAR = "CruyffCondensedRegular";
const FONT_JALLIYA = "JalliyaSeven";

type PlayerCardProps = {
  player: BasePlayer;
  position: string;
  /** Card-version art + colours, keyed by versionId. Supplied by the host. */
  versionAssets: VersionAssets;
  /** Base URL for flag/asset images (e.g. https://assets.easysbc.io). */
  assetsBaseUrl: string;
  scale?: number;
  isEvolution?: boolean;
  hidePrice?: boolean;
  /**
   * Applied chemstyle, shown as its UltimateTeamIcons glyph under the
   * position (the squad-builder card's treatment). Hidden when omitted.
   */
  chemStyleId?: number;
  /** A first-owner club card: EA's first-owner glyph under the position. Defaults to `player.firstOwner`. */
  firstOwner?: boolean;
  /** Plays the card's effects once on arrival (see CardEffects). */
  playOnShow?: boolean;
  /**
   * Localized position / attribute labels, supplied by the connected wrapper.
   * Omitted, the card falls back to the raw position and English attribute
   * codes, so this component needs no Redux store.
   */
  contentLabels?: ContentLabels;
  /** Event token catalog for token price icons, supplied by the connected wrapper. */
  eventTokens?: EventToken[];
  /**
   * The player's chemistry (0–3), as a ChemistryChip on the card's bottom-left
   * corner, just overlapping it. Hidden when omitted.
   */
  chemistry?: Chemistry;
  /**
   * The chip's own scale, apart from the card's: by default it keeps its size
   * like the meta rating chip under the card, so the two always match.
   */
  chemistryScale?: number;
};

export const PlayerCard = ({
  player,
  position,
  versionAssets,
  assetsBaseUrl,
  scale = 1,
  isEvolution,
  hidePrice,
  chemStyleId,
  firstOwner = player.firstOwner,
  playOnShow,
  contentLabels,
  eventTokens,
  chemistry,
  chemistryScale = 1,
}: PlayerCardProps) => {
  const { t } = useTranslation("common");
  const positions = contentLabels?.positions;
  const playerAttributes = contentLabels?.playerAttributes;
  const faceStatKeys =
    position === "GK" ? FACE_STAT_KEYS.gk : FACE_STAT_KEYS.outfield;
  const {
    rating,
    attributes,
    hasDynamicImage,
    playStylesPlus,
    playerUrl,
    cardName,
    cardFont,
    foil,
    cosmetics,
    versionId,
    metalId,
    countryId,
    leagueId,
    clubId,
    skillMoves,
    weakFoot,
    priceInfo,
    objectiveDisplay,
  } = player;

  const { topText, bottomText, cardUrl, isLight, strip } = extractColors({
    versionAssets,
    versionId,
    metalId,
  });

  const useFullImage = usesFullPlayerImage(hasDynamicImage, versionId);

  const flagSrc = (kind: "countries" | "leagues" | "clubs", id: number) => {
    const isDark = kind !== "countries" && !isLight;
    return `${assetsBaseUrl}/${GAME_ASSET_SLUG}/${kind}/${isDark ? "dark/" : ""}${id}.png`;
  };

  return (
    <div
      className="relative"
      style={{ width: BASE_WIDTH * scale, height: BASE_HEIGHT * scale }}
    >
      {chemistry != null && (
        <ChemistryChip
          as="span"
          chemistry={chemistry}
          className="absolute bottom-1/20 left-0 z-10 origin-bottom-left -translate-x-1/6"
          style={{ scale: chemistryScale }}
        />
      )}
      <div
        className={"relative"}
        style={{
          width: BASE_WIDTH,
          height: BASE_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          backgroundImage: `url(${cardUrl})`,
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Over the version art, under every sibling below — which must be positioned. */}
        <CardEffects
          foil={foil}
          cosmetics={cosmetics}
          cardUrl={cardUrl}
          playOnShow={playOnShow}
        />
        <img
          src={playerUrl + "?v5"}
          alt={cardName}
          className={
            useFullImage
              ? "absolute -top-1 left-0 w-full pointer-events-none"
              : "absolute top-[17%] left-[57%] w-[65%] -translate-x-1/2 pointer-events-none"
          }
        />
        {!hidePrice && (
          <CardPrice
            priceInfo={priceInfo}
            objectiveDisplay={objectiveDisplay}
            eventTokens={eventTokens}
            assetsBaseUrl={assetsBaseUrl}
          />
        )}
        <div
          className="absolute top-[21%] left-[13%] flex flex-col leading-none"
          style={{ color: topText }}
        >
          <span
            className="text-3xl"
            style={{ fontFamily: FONT_NUMBERS_MEDIUM }}
          >
            {rating}
          </span>
          <span
            className="-mt-2 mx-auto text-sm tracking-wide"
            style={{ fontFamily: FONT_CONDENSED }}
          >
            {positions?.[position]?.name ?? position}
          </span>
          {chemStyleId != null && (
            <span
              className="mx-auto text-xs -mt-1"
              style={{ fontFamily: "UltimateTeamIcons" }}
            >
              {chemStylesToIcon[chemStyleId as keyof typeof chemStylesToIcon]}
            </span>
          )}
          {firstOwner && (
            <span
              className="mx-auto text-xs -mt-1"
              style={{ fontFamily: "UltimateTeamIcons" }}
            >
              {FIRST_OWNER_GLYPH}
            </span>
          )}
        </div>
        <CardPlayStylesPlus {...{ playStylesPlus, bottomText, strip }} />
        <CardSkillMovesWeakFoot {...{ skillMoves, weakFoot, topText, strip }} />
        <div
          className="absolute inset-x-[10%] bottom-[12.5%] flex flex-col items-center gap-y-1"
          style={{ color: bottomText }}
        >
          <div
            className={cn(
              // Font size first: tailwind-merge drops `leading-*` that
              // precedes a `text-<size>`.
              cardFont === "jalliya" ? "text-xl" : "text-base",
              "leading-none truncate max-w-32",
              // An autograph card has no face stats; the signature takes
              // their room. The padding keeps its tall strokes inside the
              // clip box.
              cardFont === "jalliya" &&
                "p-[0.5em] -m-[0.5em] -mb-1 translate-y-3.5",
            )}
            style={{
              fontFamily: cardFont === "jalliya" ? FONT_JALLIYA : FONT_MEDIUM,
            }}
          >
            {cardName}
          </div>
          <div
            className={cn(
              "flex gap-x-1.75 -mt-1.5",
              // An autograph card shows no face stats; the row still sizes the
              // bottom area, so the signature and the crests sit where the name
              // and crests do on a normal card. `hidden` would collapse it and
              // drop both onto the badges.
              cardFont === "jalliya" && "invisible",
            )}
          >
            {attributes.map((value, i) => (
              <div key={i} className="flex flex-col items-center">
                <span
                  className="text-[10px]"
                  style={{ fontFamily: FONT_REGULAR }}
                >
                  {playerAttributes?.[faceStatKeys[i]]?.short ?? ATTRS[i]}
                </span>
                <span
                  className="text-[15px] -mt-1.75 font-bold"
                  style={{ fontFamily: FONT_REGULAR }}
                >
                  {value}
                </span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-x-1.5 -mt-1.5">
            <img
              alt={t("alt.country")}
              src={flagSrc("countries", countryId)}
              className="h-2.5"
            />
            <img
              alt={t("alt.league")}
              src={flagSrc("leagues", leagueId)}
              className="h-3.5"
            />
            <img
              alt={t("alt.club")}
              src={flagSrc("clubs", clubId)}
              className="h-3.5"
            />
          </div>
        </div>

        {isEvolution && (
          <div className="absolute bottom-[9%] right-[11%] size-5">
            <EvolutionsIcon outlineColor={topText} backgroundColor={strip} />
          </div>
        )}
      </div>
    </div>
  );
};

// The pieces below are drawn on the card's 180px box, so any card can reuse them
export const CardPrice = (props: ComponentProps<typeof Price>) => (
  <div className="absolute top-2.5 inset-x-0 flex justify-center">
    <Price {...props} />
  </div>
);

export const CardPlayStylesPlus = ({
  playStylesPlus,
  bottomText,
  strip,
  belowChemStyle,
}: {
  playStylesPlus: number[];
  bottomText: string;
  strip: string;
  /** Starts lower so the first badge clears a chemstyle glyph */
  belowChemStyle?: boolean;
}) => (
  <div
    className={cn(
      "absolute left-[3.5%] flex flex-col",
      playStylesPlus.length > 2
        ? "top-[37%]"
        : playStylesPlus.length > 1
          ? "top-[40%]"
          : "top-[42%]",
      belowChemStyle && "translate-y-3.5",
    )}
  >
    {playStylesPlus.map((playStylePlusId, index) => (
      <div
        className={cn(
          "w-5.5",
          playStylesPlus.length > 4 && index > 0 && "-mt-0.75",
        )}
        key={playStylePlusId}
      >
        <PlayStylePlus
          primaryColor={bottomText}
          secondaryColor={strip}
          id={playStylePlusId}
        />
      </div>
    ))}
  </div>
);

export const CardSkillMovesWeakFoot = ({
  skillMoves,
  weakFoot,
  topText,
  strip,
}: {
  skillMoves: number;
  weakFoot: number;
  topText: string;
  strip: string;
}) => (
  <div
    className={cn(
      "absolute right-1 flex flex-row gap-x-1 px-0.75 text-xs rounded-sm border",
      "top-[40%]",
    )}
    style={{
      backgroundColor: strip,
      borderColor: topText,
      color: topText,
      fontFamily: FONT_CONDENSED,
    }}
  >
    <div>{skillMoves}</div>
    <div
      className="rounded-full size-0.5 my-auto"
      style={{
        backgroundColor: topText,
      }}
    />
    <div>{weakFoot}</div>
  </div>
);

/**
 * Card-shaped placeholder matching the player-card footprint, with a centered
 * plus. Used to add a player to a comparison / lineup.
 */
export const EmptyPlayerCard = ({ onClick }: { onClick?: () => void }) => {
  const { t } = useTranslation("players");
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t("card.addPlayer")}
      className={cn(
        "flex h-[250px] w-[180px] items-center justify-center rounded-[14px]",
        "border-2 border-dashed border-neutral-700",
        "bg-gradient-to-b from-neutral-800/50 to-neutral-900/50",
        "transition-colors hover:border-neutral-600 hover:from-neutral-800 hover:to-neutral-900",
      )}
    >
      <span className="flex size-11 items-center justify-center rounded-full border border-neutral-700 bg-neutral-900">
        <Plus className="size-6 text-neutral-200" />
      </span>
    </button>
  );
};

export const PlayerCardWithMetaRating = ({
  player,
  versionAssets,
  assetsBaseUrl,
  tier,
  basePlayerTier,
  href,
  onClick,
  scale = 1,
  fullWidth = false,
  hideLabel = false,
  hideBorder = false,
  hoverable = true,
  hideBackground = false,
  hidePrice = false,
  hideNewTag = false,
  hidePlayerRoleName = true,
  chemStyleId,
  playOnShow,
  contentLabels,
  eventTokens,
  playerRoleName,
  evolution,
  chemistry,
}: {
  player: Player;
  versionAssets: VersionAssets;
  assetsBaseUrl: string;
  /** Resolved meta-rating tier for this player. */
  tier: TierType;
  /** Resolved tier of the base player when this is an evolution. */
  basePlayerTier?: TierType;
  /** When set, the card links here (host builds the URL). */
  href?: string;
  /** Fires on navigation. Only wired up alongside `href`. */
  onClick?: () => void;
  scale?: number;
  fullWidth?: boolean;
  hideLabel?: boolean;
  hideBorder?: boolean;
  hidePrice?: boolean;
  hideBackground?: boolean;
  hoverable?: boolean;
  hideNewTag?: boolean;
  hidePlayerRoleName?: boolean;
  /** Applied chemstyle glyph under the position (see PlayerCard). */
  chemStyleId?: number;
  /** Plays the card's effects once on arrival (see PlayerCard). */
  playOnShow?: boolean;
  /** Localized labels (see PlayerCard). */
  contentLabels?: ContentLabels;
  /** Event token catalog (see PlayerCard). */
  eventTokens?: EventToken[];
  /** Resolved player-role name, shown under the card unless hidden. */
  playerRoleName?: string;
  /** Chain / Cost / Games footer for an evolution player, as on mobile. */
  evolution?: EvolutionFooterDetails;
  /** The player's chemistry (0–3), on the card's bottom-left corner. */
  chemistry?: Chemistry;
}) => {
  const isEvolution = isEvolutionFunction(player);
  const basePlayer = isEvolutionPlayer(player) ? player.basePlayer : undefined;
  const showNewTag = !hideNewTag && player.newPlayer;

  const content = (
    <Card
      hideBorder={hideBorder}
      hoverable={hoverable}
      className={cn(
        "relative flex flex-col items-center h-full justify-end",
        fullWidth && "w-full",
        hideBorder ? "p-0" : "p-3",
        hideBackground && "bg-transparent",
      )}
    >
      {!hideLabel ? (
        <div className="flex flex-row justify-start items-center gap-x-2 w-full mb-1">
          <ContentTag contentType={isEvolution ? "evolution" : "player"} />
          {showNewTag && <NewTag />}
        </div>
      ) : (
        // Overlaid so new and not-new cards in a row keep the same height.
        showNewTag && (
          <div
            className={cn(
              "absolute z-10",
              hideBorder ? "top-0 left-0" : "top-3 left-3",
            )}
          >
            <NewTag />
          </div>
        )
      )}
      <PlayerCard
        scale={scale}
        player={player}
        versionAssets={versionAssets}
        assetsBaseUrl={assetsBaseUrl}
        position={player.preferredPosition}
        isEvolution={isEvolution}
        hidePrice={hidePrice}
        chemStyleId={chemStyleId}
        playOnShow={playOnShow}
        contentLabels={contentLabels}
        eventTokens={eventTokens}
        chemistry={chemistry}
      />
      <MetaRatingChip
        metaRating={player.metaRating}
        tier={tier}
        basePlayerMetaRating={basePlayer?.metaRating}
        metaRatingDelta={
          isEvolutionPlayer(player) ? player.metaRatingDelta : undefined
        }
        basePlayerTier={basePlayerTier}
      />
      {!hidePlayerRoleName && (
        <div className="text-xs mt-0.5 text-foreground-muted italic">
          {playerRoleName}
        </div>
      )}
      {evolution && (
        <EvolutionFooter {...evolution} assetsBaseUrl={assetsBaseUrl} />
      )}
    </Card>
  );
  return href ? (
    <NavLink className={cn(fullWidth && "w-full")} onClick={onClick} to={href}>
      {content}
    </NavLink>
  ) : (
    content
  );
};

export const PlayerCardWithMetaRatingSkeleton = ({
  scale = 1,
  fullWidth = false,
  hideLabel = false,
}: {
  scale?: number;
  fullWidth?: boolean;
  hideLabel?: boolean;
}) => {
  return (
    <Card
      className={cn(
        "flex flex-col items-center p-3 h-full animate-pulse",
        fullWidth && "w-full",
      )}
    >
      {!hideLabel && (
        <div className="self-start h-6 w-14 rounded-full bg-amber-900/50" />
      )}
      <div style={{ width: 180 * scale, height: 250 * scale }} />
      <div className="h-7.5 w-24 rounded-[20px] border border-neutral-600 bg-neutral-700" />
    </Card>
  );
};

export const EvolutionUpgradePlayerCard = ({
  basePlayer,
  evolutionPlayer,
  versionAssets,
  assetsBaseUrl,
  tier,
  basePlayerTier,
  scale = 0.8,
  gap = "gap-2",
  hideBackground = false,
  hidePrice = false,
  showBasePlayerMetaRating = false,
  href,
  onClick,
  className,
  contentLabels,
  eventTokens,
}: {
  basePlayer: Player;
  evolutionPlayer: Player;
  versionAssets: VersionAssets;
  assetsBaseUrl: string;
  /** Resolved meta-rating tier of the evolved player. */
  tier: TierType;
  /** Resolved tier of the base player; renders the A→S tier transition. */
  basePlayerTier?: TierType;
  scale?: number;
  /** Tailwind gap class between the cards and the arrow. */
  gap?: string;
  /** Renders bare content — no Card border, background, padding or hover. */
  hideBackground?: boolean;
  hidePrice?: boolean;
  /** Also puts the base player's own meta rating chip under its card. */
  showBasePlayerMetaRating?: boolean;
  /** Wraps the card in a NavLink. */
  href?: string;
  onClick?: () => void;
  className?: string;
  /** Localized labels (see PlayerCard). */
  contentLabels?: ContentLabels;
  /** Event token catalog (see PlayerCard). */
  eventTokens?: EventToken[];
}) => {
  const content = (
    <Card
      hideBorder={hideBackground}
      hoverable={!hideBackground}
      className={cn(
        "flex flex-row items-start justify-center h-full",
        gap,
        hideBackground ? "bg-transparent py-1" : "p-3",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-2">
        <PlayerCard
          scale={scale}
          player={basePlayer}
          versionAssets={versionAssets}
          assetsBaseUrl={assetsBaseUrl}
          position={basePlayer.preferredPosition}
          isEvolution={isEvolutionFunction(basePlayer)}
          hidePrice={hidePrice}
          contentLabels={contentLabels}
          eventTokens={eventTokens}
        />
        {showBasePlayerMetaRating && (
          <MetaRatingChip
            metaRating={basePlayer.metaRating}
            tier={basePlayerTier}
          />
        )}
      </div>
      {/* Arrow centres against the cards, not the chip-extended column. */}
      <div
        className="flex items-center"
        style={{ height: BASE_HEIGHT * scale }}
      >
        <div className="size-3 -rotate-90 text-dark-tertiary -mx-1">
          {arrow}
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <PlayerCard
          scale={scale}
          player={evolutionPlayer}
          versionAssets={versionAssets}
          assetsBaseUrl={assetsBaseUrl}
          position={evolutionPlayer.preferredPosition}
          isEvolution={isEvolutionFunction(evolutionPlayer)}
          hidePrice={hidePrice}
          contentLabels={contentLabels}
          eventTokens={eventTokens}
        />
        <MetaRatingChip
          metaRating={evolutionPlayer.metaRating}
          tier={tier}
          // A gain under 0.05 rounds to 0.0 (or -0.0), so no bonus is shown.
          basePlayerMetaRating={
            evolutionPlayer.metaRating - basePlayer.metaRating >= 0.05
              ? basePlayer.metaRating
              : undefined
          }
          basePlayerTier={basePlayerTier}
        />
      </div>
    </Card>
  );
  return href ? (
    <NavLink className="block" onClick={onClick} to={href}>
      {content}
    </NavLink>
  ) : (
    content
  );
};

export default PlayerCard;
